import type { DataWinner, IExperimentConfig, IWinnerConfig } from '../types';
import type { IGroupAdapter } from './useGroup';

import { useState, useMemo, useCallback } from 'react';
import { useExperiment } from './useExperiment';

export interface IDetermineWinnerResult extends DataWinner {
  winner: IGroupAdapter | null;
  loser: IGroupAdapter | null;
  getMinimumConfidence: () => number;
  setMinimumConfidence: (confidence: number) => void;
  getMinimumDetectableEffect: () => number;
  setMinimumDetectableEffect: (effect: number) => void;
}
export function useDetermineWinner(
  initialConfig: IExperimentConfig,
  config: IWinnerConfig = {},
): IDetermineWinnerResult {
  const [minimumConfidence, setMinimumConfidence] = useState(config.minimumConfidence ?? 0.95);
  const [minimumDetectableEffect, setMinimumDetectableEffect] = useState(
    config.minimumDetectableEffect ?? 20,
  );

  const experiment = useExperiment(initialConfig);

  const erf = (x: number): number => {
    const cof = [
      // eslint-disable-next-line @typescript-eslint/no-loss-of-precision
      -1.3026537197817094, 6.4196979235649026e-1, 1.9476473204185836e-2, -9.561514786808631e-3,
      -9.46595344482036e-4, 3.66839497852761e-4, 4.2523324806907e-5, -2.0278578112534e-5,
      -1.624290004647e-6, 1.30365583558e-6, 1.5626441722e-8, -8.5238095915e-8, 6.529054439e-9,
      5.059343495e-9, -9.91364156e-10, -2.27365122e-10, 9.6467911e-11, 2.394038e-12, -6.886027e-12,
      8.94487e-13, 3.13092e-13, -1.12708e-13, 3.81e-16, 7.106e-15, -1.523e-15, -9.4e-17, 1.21e-16,
      -2.8e-17,
    ];

    let isneg = false;
    let d = 0;
    let dd = 0;

    if (x < 0) {
      x = -x;
      isneg = true;
    }

    const t = 2 / (2 + x);
    const ty = 4 * t - 2;

    for (let j = cof.length - 1; j > 0; j--) {
      const tmp = d;
      d = ty * d - dd + cof[j];
      dd = tmp;
    }

    const res = t * Math.exp(-x * x + 0.5 * (cof[0] + ty * d) - dd);

    return isneg ? res - 1 : 1 - res;
  };

  const cdf = useCallback((zScore: number, mean: number, std: number): number => {
    return 0.5 * (1 + erf((zScore - mean) / Math.sqrt(2 * std * std)));
  }, []);

  const calculateSampleSize = (
    controlConversionRate: number,
    minimumConfidence: number,
    minimumEffect: number,
  ): number => {
    if (controlConversionRate <= 0) {
      return Infinity;
    }

    const confidence = 1 - minimumConfidence;
    const effect = controlConversionRate * (minimumEffect / 100);

    const c1 = controlConversionRate;
    const c2 = controlConversionRate - effect;
    const c3 = controlConversionRate + effect;

    const theta = Math.abs(effect);

    const variance1 = c1 * (1 - c1) + c2 * (1 - c2);
    const variance2 = c1 * (1 - c1) + c3 * (1 - c3);

    const estimate1 =
      (2 * (1 - confidence) * variance1 * Math.log(1 + Math.sqrt(variance1) / theta)) /
      (theta * theta);
    const estimate2 =
      (2 * (1 - confidence) * variance2 * Math.log(1 + Math.sqrt(variance2) / theta)) /
      (theta * theta);

    const sampleSize = Math.abs(estimate1) >= Math.abs(estimate2) ? estimate1 : estimate2;

    if (sampleSize < 0 || isNaN(sampleSize)) {
      return Infinity;
    }

    const n = Math.round(sampleSize);
    const m = Math.pow(10, 2 - Math.floor(Math.log10(n)) - 1);

    return Math.round(Math.round(n * m) / m);
  };

  const calculationResult = useMemo(() => {
    const control = experiment.getControl();
    const variation = experiment.getVariation();

    const controlViews = control.getViews();
    const controlConversions = control.getConversions();
    const controlConversionRate = control.getConversionRate();

    const variationViews = variation.getViews();
    const variationConversions = variation.getConversions();
    const variationConversionRate = variation.getConversionRate();

    let winner: number | null = null;
    let confidence = 0;
    let confident = false;
    let sampleSize = Infinity;
    let significant = false;

    if (
      controlViews !== 0 &&
      variationViews !== 0 &&
      !(controlConversions === 0 && variationConversions === 0) &&
      controlConversionRate !== variationConversionRate
    ) {
      let crA = controlConversionRate;
      let crB = variationConversionRate;

      winner = crA > crB ? 0 : 1;

      if (winner === 0) {
        [crA, crB] = [crB, crA];
      }

      const seA = Math.sqrt((crA * (1 - crA)) / controlViews);
      const seB = Math.sqrt((crB * (1 - crB)) / variationViews);
      const seDiff = Math.sqrt(Math.pow(seA, 2) + Math.pow(seB, 2));

      if (crB - crA !== 0 && seDiff !== 0) {
        const zScore = (crB - crA) / seDiff;
        confidence = cdf(zScore, 0, 1);
        confident = confidence >= minimumConfidence;
      }

      sampleSize = calculateSampleSize(crA, minimumConfidence, minimumDetectableEffect);
      significant = Math.min(controlViews, variationViews) >= sampleSize ? confident : false;
    }

    return { winner, confidence, confident, sampleSize, significant };
  }, [cdf, experiment, minimumConfidence, minimumDetectableEffect]);

  const winnerGroup =
    calculationResult.winner !== null ? experiment.getGroup(calculationResult.winner) : null;
  const loserGroup =
    calculationResult.winner !== null ? experiment.getGroup(1 - calculationResult.winner) : null;

  const getConversionRateChange = useCallback((): number => {
    const controlConversions = experiment.getControl().getConversions();
    const variationConversions = experiment.getVariation().getConversions();

    if (!controlConversions || !variationConversions) {
      return 0;
    }

    return Number(
      (((variationConversions - controlConversions) / controlConversions) * 100).toFixed(2),
    );
  }, [experiment]);

  const confidence = useMemo(() => {
    if (Number.isNaN(calculationResult.confidence)) {
      return 0;
    }

    return calculationResult.confidence;
  }, [calculationResult.confidence]);

  const dataWinner: DataWinner = useMemo(() => {
    return {
      conversionRateChange: getConversionRateChange(),
      confidence,
      minimumSampleSize: calculationResult.sampleSize,
      confident: calculationResult.confident,
      significant: calculationResult.significant,
      values: [
        {
          variantID: winnerGroup?.getVariantID(),
          confidence,
          conversionRate: winnerGroup?.getConversionRate(),
        },
        {
          variantID: loserGroup?.getVariantID(),
          confidence: confidence ? 1 - confidence : 0,
          conversionRate: loserGroup?.getConversionRate(),
        },
      ],
    };
  }, [
    calculationResult.confident,
    calculationResult.sampleSize,
    calculationResult.significant,
    confidence,
    getConversionRateChange,
    loserGroup,
    winnerGroup,
  ]);

  return {
    ...dataWinner,
    winner: winnerGroup,
    loser: loserGroup,
    getMinimumConfidence: () => minimumConfidence,
    setMinimumConfidence: (confidence: number) => setMinimumConfidence(confidence),
    getMinimumDetectableEffect: () => minimumDetectableEffect,
    setMinimumDetectableEffect: (effect: number) => setMinimumDetectableEffect(effect),
  };
}
